// SPDX-License-Identifier: MIT
// FeatureHoverTooltip – shows structured annotation data on hover; right-click opens editor
//
// Architectural notes (race-condition fix):
// -  Position is set via inline `style` (not styled-component props) to avoid
//    generating a new CSS class on every mousemove — that overhead was the main
//    source of perceived lag when transitioning between polygons.
// -  The mousemove handler is throttled to one hit-test per animation frame via
//    `requestAnimationFrame`.  Each new event cancels the previous pending frame
//    so only the *latest* cursor position is processed.
// -  Deck.gl tooltip suppression uses `useLayoutEffect` (fires before paint) and
//    a ref to track suppressed state, avoiding the cleanup→restore flash that
//    `useEffect` causes between renders.

import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { WebMercatorViewport } from '@math.gl/web-mercator';
import type { Polygon, MultiPolygon } from 'geojson';
import type { AnyAction } from 'redux';
import { toggleSidePanel, wrapTo } from '@kepler.gl/actions';
import { selectEditorFeatures } from '../../../selectors/editorFeatures';
import { selectAllAnnotations, setSelectedFeature } from '../feature-annotations-slice';
import type { FeatureAnnotation } from '../feature-annotations-slice';
import type { KeplerEditorFeature } from '../../../types/kepler-features';
import type { AppDispatch, RootState } from '../../../store';
import type { RootStateWithAnnotations } from '../feature-annotations-slice';
import store from '../../../store';

// ─── Type helpers ────────────────────────────────────────────────────────────

const asAction = <T extends { type: string }>(action: T): AnyAction =>
  action as unknown as AnyAction;

interface TooltipState {
  x: number;
  y: number;
  featureId: string;
  label: string;
  annotation: FeatureAnnotation;
}

// Kepler instance id used in App.tsx
const KEPLER_INSTANCE_ID = 'map';
const MAP_CONTAINER_SELECTOR = `#kepler-gl__${KEPLER_INSTANCE_ID}`;

// ─── Selectors from store state ───────────────────────────────────────────────

interface MapViewState {
  latitude: number;
  longitude: number;
  zoom: number;
  bearing: number;
  pitch: number;
}

function getMapViewState(state: RootState): MapViewState | null {
  const keplerState = (state as unknown as Record<string, unknown>).keplerGl;
  const ms = (keplerState as any)?.map?.mapState;
  if (!ms || typeof ms.latitude !== 'number') return null;
  return {
    latitude: ms.latitude,
    longitude: ms.longitude,
    zoom: ms.zoom,
    bearing: ms.bearing ?? 0,
    pitch: ms.pitch ?? 0,
  };
}

function screenToLngLat(
  clientX: number,
  clientY: number,
  viewState: MapViewState,
  mapRect: DOMRect
): [number, number] | null {
  const pixelX = clientX - mapRect.left;
  const pixelY = clientY - mapRect.top;

  if (pixelX < 0 || pixelY < 0 || pixelX >= mapRect.width || pixelY >= mapRect.height) {
    return null;
  }

  const viewport = new WebMercatorViewport({
    width: mapRect.width,
    height: mapRect.height,
    latitude: viewState.latitude,
    longitude: viewState.longitude,
    zoom: viewState.zoom,
    bearing: viewState.bearing,
    pitch: viewState.pitch,
  });

  const [lng, lat] = viewport.unproject([pixelX, pixelY]);
  return [lng, lat];
}

// ─── Hit testing ─────────────────────────────────────────────────────────────

function getFeatureLabel(feature: KeplerEditorFeature, index: number): string {
  const type = feature.geometry?.type || 'Feature';
  const num = index + 1;
  const idHint = feature.id ? ` (${String(feature.id).slice(0, 6)})` : '';
  return `${type} #${num}${idHint}`;
}

function findFeatureAtCoordinate(
  lngLat: [number, number],
  features: KeplerEditorFeature[]
): { feature: KeplerEditorFeature; index: number } | null {
  for (let i = 0; i < features.length; i++) {
    const feature = features[i];
    const geomType = feature.geometry?.type;
    if (geomType !== 'Polygon' && geomType !== 'MultiPolygon') continue;
    try {
      const inside = booleanPointInPolygon(lngLat, feature.geometry as Polygon | MultiPolygon);
      if (inside) return { feature, index: i };
    } catch {
      // skip malformed geometries
    }
  }
  return null;
}

/** Check if a structured annotation has any visible content */
function hasAnnotationContent(ann: FeatureAnnotation): boolean {
  return (
    !!ann.label ||
    ann.tags.length > 0 ||
    !!ann.comment ||
    !!ann.author ||
    Object.keys(ann.customProperties).length > 0
  );
}

// ─── Styled components ────────────────────────────────────────────────────────

const TOOLTIP_OFFSET_X = 14;
const TOOLTIP_OFFSET_Y = -8;
const TOOLTIP_WIDTH = 240;
const TOOLTIP_FLIP_MARGIN = 40;

const TooltipContainer = styled.div`
  position: fixed;
  z-index: 10000;
  pointer-events: none;
  width: ${TOOLTIP_WIDTH}px;
  background: ${props => props.theme.sidePanelBg};
  border: 1px solid ${props => props.theme.borderColor};
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  overflow: hidden;
`;

const TooltipHeader = styled.div`
  padding: 5px 10px;
  background: ${props => props.theme.sidePanelHeaderBg};
  border-bottom: 1px solid ${props => props.theme.borderColor};
  color: ${props => props.theme.titleTextColor};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TooltipBody = styled.div`
  padding: 4px 0;
`;

const TooltipTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
`;

const TooltipRow = styled.tr`
  &:not(:last-child) td {
    border-bottom: 1px solid ${props => props.theme.borderColor}44;
  }
`;

const TooltipKey = styled.td`
  padding: 4px 10px;
  color: ${props => props.theme.subtextColor};
  font-weight: 600;
  width: 40%;
  word-break: break-all;
  vertical-align: top;
`;

const TooltipValue = styled.td`
  padding: 4px 10px;
  color: ${props => props.theme.textColor};
  word-break: break-word;
  vertical-align: top;
`;

const TagChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  padding: 4px 10px;
`;

const TagChip = styled.span`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 10px;
  background: ${props => props.theme.primaryColor}33;
  color: ${props => props.theme.primaryColor};
`;

const RightClickHint = styled.div`
  padding: 4px 10px 5px;
  color: ${props => props.theme.subtextColor};
  font-size: 10px;
  border-top: 1px solid ${props => props.theme.borderColor}44;
  font-style: italic;
`;

// ─── Deck.gl tooltip suppression helper ───────────────────────────────────────

function setDeckTooltipVisibility(hidden: boolean): void {
  document.querySelectorAll<HTMLElement>('.deck-tooltip').forEach(el => {
    el.style.visibility = hidden ? 'hidden' : '';
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

export const FeatureHoverTooltip: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const rafRef = useRef<number>(0);
  const deckSuppressedRef = useRef(false);
  const mapElRef = useRef<Element | null>(null);

  const getLngLatFromPixel = useCallback(
    (clientX: number, clientY: number): [number, number] | null => {
      const state = store.getState();
      const viewState = getMapViewState(state);
      if (!viewState) return null;

      if (!mapElRef.current) {
        mapElRef.current = document.querySelector(MAP_CONTAINER_SELECTOR);
      }
      if (!mapElRef.current) return null;

      return screenToLngLat(clientX, clientY, viewState, mapElRef.current.getBoundingClientRect());
    },
    []
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const clientX = e.clientX;
      const clientY = e.clientY;

      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const lngLat = getLngLatFromPixel(clientX, clientY);
        if (!lngLat) {
          setTooltip(null);
          return;
        }

        const state = store.getState();
        const features = selectEditorFeatures(state);
        const allAnnotations = selectAllAnnotations(state as unknown as RootStateWithAnnotations);

        const hit = findFeatureAtCoordinate(lngLat, features);
        if (!hit) {
          setTooltip(null);
          return;
        }

        const featureId = hit.feature.id != null ? String(hit.feature.id) : '';
        const annotation = featureId ? (allAnnotations[featureId] ?? null) : null;

        if (!annotation || !hasAnnotationContent(annotation)) {
          setTooltip(null);
          return;
        }

        setTooltip({
          x: clientX,
          y: clientY,
          featureId,
          label: annotation.label || getFeatureLabel(hit.feature, hit.index),
          annotation,
        });
      });
    },
    [getLngLatFromPixel]
  );

  const handleContextMenu = useCallback(
    (e: MouseEvent) => {
      const lngLat = getLngLatFromPixel(e.clientX, e.clientY);
      if (!lngLat) return;

      const state = store.getState();
      const features = selectEditorFeatures(state);
      const hit = findFeatureAtCoordinate(lngLat, features);
      if (!hit || hit.feature.id == null) return;

      e.preventDefault();
      const featureId = String(hit.feature.id);
      dispatch(asAction(setSelectedFeature(featureId)));
      dispatch(wrapTo('map', toggleSidePanel('annotations')) as unknown as AnyAction);
      setTooltip(null);
    },
    [dispatch, getLngLatFromPixel]
  );

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('contextmenu', handleContextMenu);
      cancelAnimationFrame(rafRef.current);
    };
  }, [handleMouseMove, handleContextMenu]);

  useLayoutEffect(() => {
    if (tooltip && !deckSuppressedRef.current) {
      deckSuppressedRef.current = true;
      setDeckTooltipVisibility(true);
    } else if (!tooltip && deckSuppressedRef.current) {
      deckSuppressedRef.current = false;
      setDeckTooltipVisibility(false);
    }
  }, [tooltip]);

  useEffect(
    () => () => {
      if (deckSuppressedRef.current) {
        setDeckTooltipVisibility(false);
      }
    },
    []
  );

  if (!tooltip) return null;

  const { annotation } = tooltip;
  const flipX =
    tooltip.x + TOOLTIP_WIDTH + TOOLTIP_OFFSET_X + TOOLTIP_FLIP_MARGIN > window.innerWidth;

  return (
    <TooltipContainer
      style={{
        left: flipX ? tooltip.x - TOOLTIP_WIDTH - TOOLTIP_OFFSET_X : tooltip.x + TOOLTIP_OFFSET_X,
        top: tooltip.y + TOOLTIP_OFFSET_Y,
      }}
      data-testid='feature-hover-tooltip'
      role='tooltip'
      aria-label={`Annotations for ${tooltip.label}`}
    >
      <TooltipHeader title={tooltip.label}>{tooltip.label}</TooltipHeader>
      <TooltipBody>
        {annotation.tags.length > 0 && (
          <TagChips>
            {annotation.tags.map(tag => (
              <TagChip key={tag}>{tag}</TagChip>
            ))}
          </TagChips>
        )}
        <TooltipTable>
          <tbody>
            {annotation.comment && (
              <TooltipRow>
                <TooltipKey>comment</TooltipKey>
                <TooltipValue>{annotation.comment}</TooltipValue>
              </TooltipRow>
            )}
            {annotation.author && (
              <TooltipRow>
                <TooltipKey>author</TooltipKey>
                <TooltipValue>{annotation.author}</TooltipValue>
              </TooltipRow>
            )}
            {Object.entries(annotation.customProperties).map(([key, value]) => (
              <TooltipRow key={key}>
                <TooltipKey title={key}>{key}</TooltipKey>
                <TooltipValue>{value}</TooltipValue>
              </TooltipRow>
            ))}
          </tbody>
        </TooltipTable>
      </TooltipBody>
      <RightClickHint>Right-click to edit</RightClickHint>
    </TooltipContainer>
  );
};

FeatureHoverTooltip.displayName = 'FeatureHoverTooltip';

export default FeatureHoverTooltip;
